/**
 * @sazeo/sdk — Search hooks (real implementation)
 *
 * Global FTS across users, teams, folders, documents, channels, and top-level
 * messages. Debounced 300ms on the client. All requests attach the caller's
 * Supabase JWT — the Edge Function enforces tenant isolation via RLS.
 *
 * Paired spec: specs/CODEX_PHASE2_WAVE3_DOCUMENTS_SEARCH.md §2.4, §3.2
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';
import { buildFunctionUrl } from './functionUrl';

export type SearchEntityType = 'user' | 'team' | 'document' | 'folder' | 'channel' | 'message';

export interface SearchResult {
  entity_type: SearchEntityType;
  entity_id: string;
  title: string;
  subtitle: string | null;
  body_snippet: string | null;
  rank: number;
  url: string;
}

export interface SearchResponse {
  results: SearchResult[];
  grouped: Partial<Record<SearchEntityType, SearchResult[]>>;
  total: number;
}

// Read the caller JWT off the Supabase client. Unavailable in SSR/no-env
// contexts — fall back to no auth headers and let the Edge Function reject.
interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(): SupabaseAuthLike {
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return { auth: { getSession: async () => ({ data: { session: null } }) } };
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const client = resolveClient();
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

export interface UseSearchOptions {
  /** Debounce window in ms. Default 300. */
  debounceMs?: number;
  /** Restrict to specific entity types. */
  entity_types?: SearchEntityType[];
  /** Max total results requested from the server. */
  limit?: number;
}

// ---------------------------------------------------------------------------
// useSearch — debounced query; returns grouped results
// ---------------------------------------------------------------------------

const EMPTY_RESPONSE: SearchResponse = { results: [], grouped: {}, total: 0 };

export function useSearch(
  query: string,
  opts: UseSearchOptions = {},
): {
  data: SearchResponse;
  isLoading: boolean;
  error: Error | null;
} {
  const { debounceMs = 300, entity_types, limit = 20 } = opts;
  const [data, setData] = useState<SearchResponse>(EMPTY_RESPONSE);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Monotonic guard so stale responses never overwrite the freshest one.
  const reqIdRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length === 0) {
      setData(EMPTY_RESPONSE);
      setIsLoading(false);
      setError(null);
      return;
    }

    const myId = ++reqIdRef.current;
    setIsLoading(true);
    setError(null);

    const timer = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q, limit: String(limit) });
        if (entity_types && entity_types.length > 0) {
          params.set('types', entity_types.join(','));
        }
        const headers = await getAuthHeaders();
        const res = await fetch(`${buildFunctionUrl('')}search?${params.toString()}`, { headers });
        if (reqIdRef.current !== myId) return;
        if (!res.ok) {
          setData(EMPTY_RESPONSE);
          setError(new Error(`search ${res.status}`));
          setIsLoading(false);
          return;
        }
        const json = (await res.json()) as SearchResponse;
        if (reqIdRef.current !== myId) return;
        setData(json ?? EMPTY_RESPONSE);
        setIsLoading(false);
      } catch (err) {
        if (reqIdRef.current !== myId) return;
        setData(EMPTY_RESPONSE);
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      }
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [query, debounceMs, entity_types, limit]);

  return { data, isLoading, error };
}

// ---------------------------------------------------------------------------
// useSearchHistory — localStorage-backed recent queries
// ---------------------------------------------------------------------------

const HISTORY_KEY = 'sazeo.search.recent';
const HISTORY_LIMIT = 10;

function readHistory(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === 'string').slice(0, HISTORY_LIMIT);
  } catch {
    return [];
  }
}

function writeHistory(list: readonly string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, HISTORY_LIMIT)));
  } catch {
    // localStorage quota / private mode — ignore
  }
}

export function useSearchHistory(): {
  recent: string[];
  push: (query: string) => void;
  clear: () => void;
} {
  const [recent, setRecent] = useState<string[]>(() => readHistory());

  const push = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setRecent((prev) => {
      const deduped = [trimmed, ...prev.filter((q) => q !== trimmed)].slice(0, HISTORY_LIMIT);
      writeHistory(deduped);
      return deduped;
    });
  }, []);

  const clear = useCallback(() => {
    writeHistory([]);
    setRecent([]);
  }, []);

  return { recent, push, clear };
}
